import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { api, apiError, waktu } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ErpLayout, Card, Table } from "@/erp/shell";
import { toast } from "sonner";
import { Plus, Trash2, X, Network, Upload, Download, Users2, KeyRound, Pencil, Send, Search, UserCheck, UserX, CheckCircle, XCircle, AlertTriangle, Eye, Clock } from "lucide-react";

const inp = "w-full rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const label = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

export function PartnersAdmin() {
  const { can, user: current } = useAuth();
  const [tree, setTree] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bForm, setBForm] = useState(null);
  const [pForm, setPForm] = useState(null); // { branch_id, selectedUser }
  const [adminForm, setAdminForm] = useState(null); // { branch }
  const [adminList, setAdminList] = useState(null); // { branch, admins }
  const [refForm, setRefForm] = useState(null); // { partner }
  // Pencarian Jamaah untuk promosi UC
  const [jamaahSearch, setJamaahSearch] = useState("");
  const [jamaahResults, setJamaahResults] = useState([]);
  const [jamaahLoading, setJamaahLoading] = useState(false);
  const searchTimer = useRef(null);
  const isSuperAdmin = current?.roles?.includes("super_admin");

  const subBranchList = useMemo(
    () => branches.filter((b) => b.type === "cabang_pembantu"),
    [branches],
  );
  const parentBranches = useMemo(() => branches.filter((b) => b.type === "cabang"), [branches]);

  const load = useCallback(() => {
    api.get("/admin/hierarchy").then((r) => setTree(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBranch = async (e) => {
    e.preventDefault();
    try {
      if (bForm.id) {
        await api.put(`/admin/branches/${bForm.id}`, { ...bForm, parent_id: bForm.parent_id || null });
        toast.success("Cabang berhasil diperbarui");
      } else {
        await api.post("/admin/branches", { ...bForm, parent_id: bForm.parent_id || null });
        toast.success("Cabang dibuat");
      }
      setBForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const savePartner = async (e) => {
    e.preventDefault();
    if (!pForm?.selectedUser) { toast.error("Pilih pengguna terlebih dahulu"); return; }
    if (!pForm?.branch_id) { toast.error("Pilih Cabang Pembantu terlebih dahulu"); return; }
    try {
      await api.post("/admin/partners/from-jamaah", {
        user_id: pForm.selectedUser.id,
        branch_id: pForm.branch_id,
      });
      toast.success(`${pForm.selectedUser.full_name} berhasil dijadikan Umrah Consultant`);
      setPForm(null); setJamaahSearch(""); setJamaahResults([]); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const searchJamaah = (q) => {
    setJamaahSearch(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setJamaahResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setJamaahLoading(true);
      try {
        const { data } = await api.get(`/admin/partners/jamaah-candidates?q=${encodeURIComponent(q)}`);
        setJamaahResults(data);
      } catch { setJamaahResults([]); }
      finally { setJamaahLoading(false); }
    }, 350);
  };
  const delBranch = async (id) => {
    if (!window.confirm("Hapus cabang ini?")) return;
    try { await api.delete(`/admin/branches/${id}`); toast.success("Cabang dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delPartner = async (id) => {
    if (!window.confirm("Hapus Umrah Consultant ini? Tindakan ini tidak akan mengubah akun pengguna terkait.")) return;
    try { await api.delete(`/admin/partners/${id}`); toast.success("Mitra dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const demoteToJamaah = async (c) => {
    if (!window.confirm(`Turunkan "${c.full_name}" kembali menjadi Jamaah?\nRole Umrah Consultant, kode referral, dan profil mitra akan dihapus. Akun pengguna tetap aktif sebagai Jamaah.`)) return;
    try {
      await api.post(`/admin/partners/${c.id}/demote-to-jamaah`);
      toast.success(`${c.full_name} berhasil diturunkan menjadi Jamaah`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openAdmins = async (branch) => {
    try {
      const { data } = await api.get(`/admin/branches/${branch.id}/admins`);
      setAdminList({ branch, admins: data });
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveAdmin = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/branches/${adminForm.branch.id}/admins`, {
        full_name: adminForm.full_name, email: adminForm.email, phone: adminForm.phone,
      });
      toast.success("Admin dibuat. Email undangan + tautan reset password telah dikirim.");
      const refresh = await api.get(`/admin/branches/${adminForm.branch.id}/admins`);
      setAdminList({ branch: adminForm.branch, admins: refresh.data });
      setAdminForm(null);
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const delAdmin = async (branch, uid) => {
    if (!window.confirm("Hapus admin ini?")) return;
    try {
      await api.delete(`/admin/branches/${branch.id}/admins/${uid}`);
      const refresh = await api.get(`/admin/branches/${branch.id}/admins`);
      setAdminList({ branch, admins: refresh.data });
      toast.success("Admin dihapus");
    } catch (e) { toast.error(apiError(e)); }
  };
  const resendReset = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/send-reset`);
      toast.success("Tautan reset password dikirim ulang");
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveReferral = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/partners/${refForm.partner.id}/referral-code`, { code: refForm.code });
      toast.success("Kode referral diperbarui");
      setRefForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  return (
    <ErpLayout title="Mitra & Komisi" breadcrumb={["Mitra & Komisi"]}
      actions={can("partner.write") && (<>
        <button onClick={() => setBForm({ name: "", type: "cabang", parent_id: "", city: "", address: "", phone: "", code: "" })}
          data-testid="branch-new-btn" className="rounded-xl border border-[#141414] px-4 py-2.5 text-sm font-semibold text-[#141414]">
          <Plus size={14} className="mr-1 inline" /> Cabang
        </button>
        <button onClick={() => { setPForm({ branch_id: subBranchList[0]?.id || "", selectedUser: null }); setJamaahSearch(""); setJamaahResults([]); }}
          data-testid="partner-new-btn" className="btn-gold rounded-xl px-4 py-2.5 text-sm font-bold">
          <Plus size={14} className="mr-1 inline" /> Umrah Consultant
        </button></>)}>
      <Card className="mb-5">
        <div className="flex items-start gap-3">
          <Network className="mt-1 text-[#C5A028]" size={20} />
          <div className="text-sm text-[#4A4844]">
            <p>
              Struktur mitra <b>wajib</b>: <b>Cabang</b> → <b>Cabang Pembantu</b> → <b>Umrah Consultant</b>.
              Umrah Consultant harus berada di bawah Cabang Pembantu.
            </p>
            <p className="mt-2">
              Pembagian pool komisi per jamaah: <b>Cabang 40%</b>, <b>Cabang Pembantu 20%</b>, <b>Umrah Consultant 40%</b>.
              Kode referral hanya dimiliki Umrah Consultant.
            </p>
          </div>
        </div>
      </Card>

      <div className="space-y-5" data-testid="hierarchy-tree">
        {tree.length === 0 && <Card className="text-sm text-[#8A867C]">Belum ada cabang.</Card>}
        {tree.map((b) => (
          <Card key={b.id} data-testid={`branch-card-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="eyebrow">Cabang · {b.code}</div>
                <h3 className="mt-1 font-display text-lg font-bold text-[#141414]">{b.name}</h3>
                <div className="text-xs text-[#8A867C]">{b.city} · {b.phone}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {can("partner.write") && (
                  <button onClick={() => openAdmins(b)} data-testid={`branch-admins-${b.id}`}
                    className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold text-[#4A4844]">
                    <Users2 size={13} className="mr-1 inline" />Kelola Admin
                  </button>)}
                {isSuperAdmin && (
                  <button onClick={() => setBForm({ id: b.id, name: b.name, type: b.type, parent_id: b.parent_id || "", city: b.city || "", address: b.address || "", phone: b.phone || "", code: b.code || "" })}
                    data-testid={`branch-edit-${b.id}`}
                    className="rounded-lg border border-[#E5DEC9] p-2 text-[#4A4844] hover:bg-[#F4EFE6]" title="Edit cabang">
                    <Pencil size={14} />
                  </button>)}
                {can("partner.write") && (
                  <button onClick={() => delBranch(b.id)} data-testid={`branch-delete-${b.id}`}
                    className="rounded-lg border border-[#F0C4C4] p-2 text-[#B91C1C]"><Trash2 size={14} /></button>)}
              </div>
            </div>

            <div className="mt-4">
              <div className="eyebrow">Cabang Pembantu ({b.sub_branches.length})</div>
              <div className="mt-2 space-y-3">
                {b.sub_branches.map((s) => (
                  <div key={s.id} className="rounded-lg bg-[#F4EFE6] p-4" data-testid={`subbranch-${s.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-bold text-[#141414]">{s.name}
                          <span className="ml-2 text-xs font-normal text-[#8A867C]">· {s.code}</span>
                        </div>
                        <div className="mt-0.5 text-xs text-[#8A867C]">{s.city} · {s.phone}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {can("partner.write") && (
                          <button onClick={() => openAdmins(s)} data-testid={`subbranch-admins-${s.id}`}
                            className="rounded-lg border border-[#E5DEC9] bg-white px-3 py-1.5 text-xs font-semibold text-[#4A4844]">
                            <Users2 size={13} className="mr-1 inline" />Admin
                          </button>)}
                        {isSuperAdmin && (
                          <button onClick={() => setBForm({ id: s.id, name: s.name, type: s.type, parent_id: s.parent_id || "", city: s.city || "", address: s.address || "", phone: s.phone || "", code: s.code || "" })}
                            data-testid={`subbranch-edit-${s.id}`}
                            className="rounded-lg border border-[#E5DEC9] bg-white p-1.5 text-[#4A4844] hover:bg-white" title="Edit cabang pembantu">
                            <Pencil size={13} />
                          </button>)}
                        {can("partner.write") && (
                          <button onClick={() => delBranch(s.id)} className="text-[#B91C1C]"><Trash2 size={13} /></button>)}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-[#4A4844]">Umrah Consultant ({s.consultants.length})</div>
                      <ul className="mt-2 space-y-2">
                        {s.consultants.map((c) => (
                          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white bg-white p-3 text-sm" data-testid={`consultant-${c.id}`}>
                            <div>
                              <div className="font-bold text-[#141414]">{c.full_name}</div>
                              <div className="mt-0.5 text-xs text-[#8A867C]">
                                {c.email}
                                <span className="ml-2 rounded-full bg-[#F5F1E8] px-2 py-0.5 font-semibold text-[#C5A028]">{c.code}</span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {isSuperAdmin && (
                                <button onClick={() => setRefForm({ partner: c, code: c.code })}
                                  data-testid={`referral-edit-${c.id}`}
                                  className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]"
                                  title="Ubah kode referral">
                                  <KeyRound size={13} />
                                </button>)}
                              {can("partner.write") && (
                                <button onClick={() => demoteToJamaah(c)}
                                  data-testid={`partner-demote-${c.id}`}
                                  className="rounded-lg border border-[#FDE68A] p-1.5 text-[#92400E] hover:bg-[#FEF9C3]"
                                  title="Turunkan ke Jamaah">
                                  <UserX size={13} />
                                </button>)}
                              {can("partner.write") && (
                                <button onClick={() => delPartner(c.id)} className="rounded-lg border border-[#F0C4C4] p-1.5 text-[#B91C1C]"><Trash2 size={13} /></button>)}
                            </div>
                          </li>))}
                        {s.consultants.length === 0 && <li className="text-xs text-[#8A867C]">Belum ada Umrah Consultant di Cabang Pembantu ini.</li>}
                      </ul>
                    </div>
                  </div>))}
                {b.sub_branches.length === 0 && <p className="text-sm text-[#8A867C]">Belum ada cabang pembantu.</p>}
              </div>
            </div>
          </Card>))}
      </div>

      {bForm && (
        <Modal title={bForm.id ? `Edit ${bForm.type === "cabang" ? "Cabang" : "Cabang Pembantu"}` : "Cabang / Cabang Pembantu Baru"} onClose={() => setBForm(null)} onSubmit={saveBranch} testid="branch-form">
          <div><label className={label}>Nama</label><input required className={inp} data-testid="bf-name" value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} /></div>
          {!bForm.id && (<div><label className={label}>Tipe</label>
            <select className={inp} data-testid="bf-type" value={bForm.type} onChange={(e) => setBForm({ ...bForm, type: e.target.value })}>
              <option value="cabang">Cabang</option><option value="cabang_pembantu">Cabang Pembantu</option>
            </select></div>)}
          {(bForm.type === "cabang_pembantu") && (
            <div><label className={label}>Cabang Induk</label>
              <select required={!bForm.id} className={inp} data-testid="bf-parent" value={bForm.parent_id} onChange={(e) => setBForm({ ...bForm, parent_id: e.target.value })}>
                <option value="">Pilih cabang</option>
                {parentBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select></div>)}
          <div><label className={label}>Kota</label><input className={inp} data-testid="bf-city" value={bForm.city} onChange={(e) => setBForm({ ...bForm, city: e.target.value })} /></div>
          <div><label className={label}>Telepon</label><input className={inp} data-testid="bf-phone" value={bForm.phone} onChange={(e) => setBForm({ ...bForm, phone: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={label}>Alamat</label><input className={inp} data-testid="bf-address" value={bForm.address} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} /></div>
          {bForm.id && isSuperAdmin && (
            <div><label className={label}>Kode Cabang</label><input className={`${inp} uppercase`} data-testid="bf-code" value={bForm.code} onChange={(e) => setBForm({ ...bForm, code: e.target.value.toUpperCase() })} /></div>)}
        </Modal>)}

      {pForm && (
        <Modal title="Tambah Umrah Consultant dari Pengguna Terdaftar" onClose={() => { setPForm(null); setJamaahSearch(""); setJamaahResults([]); }} onSubmit={savePartner} testid="partner-form">
          {/* Info */}
          <div className="sm:col-span-2 rounded-lg bg-[#F5F1E8] p-3 text-xs text-[#4A4844]">
            Pilih pengguna yang sudah terdaftar (Jamaah maupun pengguna lain). Role <b>Umrah Consultant</b> akan ditambahkan ke akun tersebut dan kode referral dibuat otomatis.
          </div>

          {/* Cari Pengguna */}
          <div className="sm:col-span-2">
            <label className={label}>Cari Pengguna Terdaftar (nama atau email)</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A867C]" />
              <input
                className={`${inp} pl-9`}
                data-testid="pf-search"
                placeholder="Ketik nama atau email pengguna..."
                value={jamaahSearch}
                onChange={(e) => searchJamaah(e.target.value)}
                autoComplete="off"
              />
            </div>

            {/* Hasil pencarian */}
            {jamaahLoading && <p className="mt-2 text-xs text-[#8A867C]">Mencari...</p>}
            {!jamaahLoading && jamaahSearch && jamaahResults.length === 0 && (
              <p className="mt-2 text-xs text-[#B91C1C]">Tidak ada pengguna yang cocok atau semua sudah menjadi Umrah Consultant.</p>
            )}
            {jamaahResults.length > 0 && (
              <ul className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-[#E5DEC9] bg-white divide-y divide-[#F4EFE6]">
                {jamaahResults.map((u) => (
                  <li key={u.id}
                    data-testid={`pf-jamaah-${u.id}`}
                    onClick={() => { setPForm((f) => ({ ...f, selectedUser: u })); setJamaahResults([]); setJamaahSearch(""); }}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F4EFE6] transition-colors ${pForm?.selectedUser?.id === u.id ? "bg-[#F5F1E8]" : ""}`}>
                    <div className="h-8 w-8 rounded-full bg-[#C5A028] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {u.full_name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#141414]">{u.full_name}</div>
                      <div className="text-xs text-[#8A867C] flex items-center gap-2 flex-wrap">
                        <span>{u.email}</span>
                        {u.roles?.length > 0 && (
                          <span className="rounded-full bg-[#F4EFE6] px-2 py-0.5 font-medium capitalize">
                            {u.roles.join(", ").replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Akun terpilih */}
          {pForm?.selectedUser && (
            <div className="sm:col-span-2 flex items-center gap-3 rounded-lg border-2 border-[#C5A028] bg-[#FEFCF7] p-3">
              <UserCheck size={18} className="text-[#C5A028] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[#141414]">{pForm.selectedUser.full_name}</div>
                <div className="text-xs text-[#8A867C] truncate">{pForm.selectedUser.email}</div>
              </div>
              <button type="button" onClick={() => setPForm((f) => ({ ...f, selectedUser: null }))}
                className="text-[#8A867C] hover:text-[#B91C1C]"><X size={14} /></button>
            </div>
          )}

          {/* Pilih Cabang Pembantu */}
          <div className="sm:col-span-2">
            <label className={label}>Ditempatkan di Cabang Pembantu</label>
            <select required className={inp} data-testid="pf-branch" value={pForm.branch_id}
              onChange={(e) => setPForm((f) => ({ ...f, branch_id: e.target.value }))}>
              <option value="">Pilih Cabang Pembantu</option>
              {subBranchList.map((b) => {
                const parent = parentBranches.find((p) => p.id === b.parent_id);
                return <option key={b.id} value={b.id}>{parent?.name ? `${parent.name} → ` : ""}{b.name}</option>;
              })}
            </select>
            {subBranchList.length === 0 && <p className="mt-2 text-xs text-[#B91C1C]">Belum ada Cabang Pembantu. Buat Cabang Pembantu terlebih dahulu.</p>}
          </div>
        </Modal>)}

      {adminList && (
        <BranchAdminsModal
          data={adminList}
          onClose={() => setAdminList(null)}
          onAdd={() => setAdminForm({ branch: adminList.branch, full_name: "", email: "", phone: "" })}
          onDelete={(uid) => delAdmin(adminList.branch, uid)}
          onResend={resendReset}
        />)}

      {adminForm && (
        <Modal
          title={`Tambah Admin — ${adminForm.branch.name}`}
          onClose={() => setAdminForm(null)}
          onSubmit={saveAdmin}
          testid="branch-admin-form">
          <div className="sm:col-span-2 rounded-lg bg-[#F5F1E8] p-3 text-xs text-[#4A4844]">
            Admin ini hanya dapat mengakses data mitra di bawah <b>{adminForm.branch.name}</b>.
            Setelah dibuat, sistem akan mengirim <b>email selamat datang + tautan reset password</b> (berlaku 24 jam).
          </div>
          <div><label className={label}>Nama Lengkap</label>
            <input required className={inp} data-testid="af-name" value={adminForm.full_name}
              onChange={(e) => setAdminForm({ ...adminForm, full_name: e.target.value })} /></div>
          <div><label className={label}>Email</label>
            <input required type="email" className={inp} data-testid="af-email" value={adminForm.email}
              onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={label}>Telepon (opsional)</label>
            <input className={inp} data-testid="af-phone" value={adminForm.phone}
              onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })} /></div>
        </Modal>)}

      {refForm && (
        <Modal title={`Ubah Kode Referral — ${refForm.partner.full_name}`}
          onClose={() => setRefForm(null)} onSubmit={saveReferral} testid="referral-form">
          <div className="sm:col-span-2 rounded-lg bg-[#F5F1E8] p-3 text-xs text-[#4A4844]">
            4–20 karakter, huruf/angka, unik antar mitra. Kode akan digunakan pada tautan referral publik.
          </div>
          <div className="sm:col-span-2"><label className={label}>Kode Referral Baru</label>
            <input required minLength={4} maxLength={20} className={inp} data-testid="ref-code"
              value={refForm.code}
              onChange={(e) => setRefForm({ ...refForm, code: e.target.value.toUpperCase() })} />
          </div>
        </Modal>)}
    </ErpLayout>
  );
}

function BranchAdminsModal({ data, onClose, onAdd, onDelete, onResend }) {
  const { branch, admins } = data;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-10 w-full max-w-2xl rounded-2xl bg-white p-7" data-testid="branch-admins-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">{branch.type === "cabang" ? "Admin Cabang" : "Admin Cabang Pembantu"}</div>
            <h2 className="mt-1 font-display text-xl font-bold text-[#141414]">{branch.name}</h2>
          </div>
          <button onClick={onClose} data-testid="branch-admins-close"><X size={18} /></button>
        </div>
        <p className="mt-3 text-sm text-[#4A4844]">
          Admin ini hanya dapat melihat/mengelola mitra di bawah <b>{branch.name}</b>.
        </p>
        <div className="mt-5 space-y-2">
          {admins.length === 0 && <div className="text-sm text-[#8A867C]">Belum ada admin.</div>}
          {admins.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] p-3 text-sm">
              <div>
                <div className="font-bold text-[#141414]">{u.full_name}</div>
                <div className="text-xs text-[#8A867C]">{u.email} · {u.phone || "-"}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onResend(u.id)} title="Kirim ulang reset password"
                  className="rounded-lg border border-[#E5DEC9] p-2 text-[#4A4844] hover:bg-[#F4EFE6]"
                  data-testid={`resend-reset-${u.id}`}>
                  <Send size={14} />
                </button>
                <button onClick={() => onDelete(u.id)}
                  className="rounded-lg border border-[#F0C4C4] p-2 text-[#B91C1C]"
                  data-testid={`delete-admin-${u.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>))}
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={onAdd} data-testid="branch-admin-add-btn"
            className="btn-gold rounded-xl px-5 py-2.5 text-sm font-bold">
            <Plus size={14} className="mr-1 inline" />Tambah Admin
          </button>
          <button onClick={onClose} className="rounded-xl border border-[#E5DEC9] px-5 py-2.5 text-sm font-semibold">Tutup</button>
        </div>
      </div>
    </div>
  );
}

export function Modal({ title, onClose, onSubmit, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <form onSubmit={onSubmit} data-testid={testid} className="my-10 w-full max-w-2xl rounded-2xl bg-white p-7">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-[#141414]">{title}</h2>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">{children}</div>
        <div className="mt-7 flex gap-3">
          <button className="btn-gold rounded-xl px-6 py-3 text-sm font-bold" data-testid={`${testid}-submit`}>Simpan</button>
          <button type="button" onClick={onClose} className="rounded-xl border border-[#E5DEC9] px-6 py-3 text-sm font-semibold">Batal</button>
        </div>
      </form>
    </div>);
}

export function UsersAdmin() {
  const { can, user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const isSuperAdmin = current?.roles?.includes("super_admin");

  const load = useCallback(() => {
    api.get("/admin/users", { params: { q: q || undefined } }).then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/admin/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/admin/permission-matrix").then((r) => setMatrix(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", { ...form, branch_id: form.branch_id || null });
      toast.success(`Undangan dibuat. Password sementara: ${data.temporary_password}`);
      setForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/users/${editForm.id}`, {
        full_name: editForm.full_name, email: editForm.email, phone: editForm.phone,
        branch_id: editForm.branch_id || null, roles: editForm.roles,
        is_active: editForm.is_active,
      });
      toast.success("Profil pengguna diperbarui");
      setEditForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.email} secara permanen? Tindakan ini tidak dapat dibatalkan.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendReset = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(`Tautan reset password dikirim ke ${u.email}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Pengguna & Hak Akses" breadcrumb={["Pengguna & Hak Akses"]}
      actions={can("user.write") && (
        <button onClick={() => setForm({ full_name: "", email: "", roles: ["operations_admin"], branch_id: "", phone: "", password: "" })}
          data-testid="user-new-btn" className="btn-gold rounded-xl px-4 py-2.5 text-sm font-bold"><Plus size={14} className="mr-1 inline" /> Undang Pengguna</button>)}>
      <Card className="mb-5">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama atau email..." data-testid="user-search" className={`${inp} max-w-sm`} />
      </Card>
      <Table testid="user-table" columns={["Nama", "Email", "Role", "Cabang", "Status", "Aksi"]}
        rows={users.map((u) => (
          <tr key={u.id} className="border-t border-[#F4EFE6]" data-testid={`user-row-${u.id}`}>
            <td className="p-4 font-semibold">{u.full_name}</td>
            <td className="p-4">{u.email}</td>
            <td className="p-4 text-sm">{u.role_labels?.join(", ")}</td>
            <td className="p-4">{branches.find((b) => b.id === u.branch_id)?.name || "-"}</td>
            <td className="p-4">
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${u.is_active ? "bg-[#F5F1E8] text-[#15803D]" : "bg-[#FBE9E9] text-[#B91C1C]"}`}>{u.is_active ? "Aktif" : "Nonaktif"}</span>
            </td>
            <td className="p-4">
              <div className="flex flex-wrap gap-1">
                {can("user.write") && (
                  <button onClick={() => setEditForm({
                    id: u.id, full_name: u.full_name, email: u.email, phone: u.phone || "",
                    branch_id: u.branch_id || "", roles: u.roles || ["jamaah"], is_active: u.is_active,
                  })} data-testid={`user-edit-${u.id}`}
                    className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]"
                    title="Edit profil"><Pencil size={13} /></button>)}
                {can("user.write") && (
                  <button onClick={() => sendReset(u)} data-testid={`user-reset-${u.id}`}
                    className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]"
                    title="Kirim tautan reset password"><Send size={13} /></button>)}
                {can("user.write") && (
                  <button onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`}
                    className="rounded-lg border border-[#E5DEC9] px-2 py-1.5 text-xs font-semibold">
                    {u.is_active ? "Off" : "On"}</button>)}
                {isSuperAdmin && u.id !== current?.id && (
                  <button onClick={() => del(u)} data-testid={`user-delete-${u.id}`}
                    className="rounded-lg border border-[#F0C4C4] p-1.5 text-[#B91C1C]"
                    title="Hapus pengguna"><Trash2 size={13} /></button>)}
              </div>
            </td>
          </tr>))} />

      {matrix && (
        <Card className="mt-7 overflow-x-auto">
          <div className="eyebrow">Permission Matrix (berlaku juga di server)</div>
          <table className="mt-4 w-full text-xs" data-testid="permission-matrix">
            <thead><tr className="text-left text-[#4A4844]"><th className="p-2">Role</th><th className="p-2">Izin</th></tr></thead>
            <tbody>
              {Object.entries(matrix.roles).map(([role, perms]) => (
                <tr key={role} className="border-t border-[#F4EFE6]">
                  <td className="p-2 font-bold text-[#141414]">{matrix.labels[role]}</td>
                  <td className="p-2 text-[#4A4844]">{perms.join(", ")}</td>
                </tr>))}
            </tbody>
          </table>
        </Card>)}

      {form && (
        <Modal title="Undang Pengguna" onClose={() => setForm(null)} onSubmit={save} testid="user-form">
          <div><label className={label}>Nama Lengkap</label><input required className={inp} data-testid="uf-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
          <div><label className={label}>Email</label><input required type="email" className={inp} data-testid="uf-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><label className={label}>Role</label>
            <select className={inp} data-testid="uf-role" value={form.roles[0]} onChange={(e) => setForm({ ...form, roles: [e.target.value] })}>
              {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select></div>
          <div><label className={label}>Cabang (opsional)</label>
            <select className={inp} data-testid="uf-branch" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
              <option value="">Tanpa cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select></div>
          <div className="sm:col-span-2"><label className={label}>Password Awal (opsional)</label>
            <input className={inp} data-testid="uf-password" placeholder="Default: Undangan#2026" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        </Modal>)}

      {editForm && (
        <Modal title={`Edit Profil — ${editForm.full_name}`} onClose={() => setEditForm(null)} onSubmit={saveEdit} testid="user-edit-form">
          <div><label className={label}>Nama Lengkap</label>
            <input required className={inp} data-testid="ue-name" value={editForm.full_name}
              onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} /></div>
          <div><label className={label}>Email</label>
            <input required type="email" className={inp} data-testid="ue-email" value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
          <div><label className={label}>Telepon</label>
            <input className={inp} data-testid="ue-phone" value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
          <div><label className={label}>Role</label>
            <select className={inp} data-testid="ue-role" value={editForm.roles[0]}
              onChange={(e) => setEditForm({ ...editForm, roles: [e.target.value] })}>
              {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select></div>
          <div><label className={label}>Cabang</label>
            <select className={inp} data-testid="ue-branch" value={editForm.branch_id}
              onChange={(e) => setEditForm({ ...editForm, branch_id: e.target.value })}>
              <option value="">Tanpa cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select></div>
          <div><label className={label}>Status</label>
            <select className={inp} data-testid="ue-active" value={editForm.is_active ? "1" : "0"}
              onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === "1" })}>
              <option value="1">Aktif</option><option value="0">Nonaktif</option>
            </select></div>
        </Modal>)}
    </ErpLayout>
  );
}

// ── Status badge helper ─────────────────────────────────────────────────────
const DOC_STATUS_STYLE = {
  approved:           "bg-green-100 text-green-800",
  uploaded:           "bg-blue-100 text-blue-800",
  under_review:       "bg-yellow-100 text-yellow-800",
  revision_required:  "bg-orange-100 text-orange-800",
  rejected:           "bg-red-100 text-red-800",
  expired:            "bg-gray-200 text-gray-600",
  not_uploaded:       "bg-gray-100 text-gray-500",
};
const DOC_STATUS_LABEL = {
  approved: "Disetujui", uploaded: "Diunggah", under_review: "Sedang Direview",
  revision_required: "Perlu Revisi", rejected: "Ditolak",
  expired: "Kedaluwarsa", not_uploaded: "Belum Diunggah",
};
function DocStatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${DOC_STATUS_STYLE[status] || "bg-gray-100 text-gray-500"}`}>
      {DOC_STATUS_LABEL[status] || status}
    </span>);
}

// ── Review Modal ────────────────────────────────────────────────────────────
function ReviewModal({ doc, onClose, onDone }) {
  const [status, setStatus] = useState("approved");
  const [note, setNote] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);

  const needsNote = ["revision_required", "rejected"].includes(status);

  const submit = async (e) => {
    e.preventDefault();
    if (needsNote && !note.trim()) return toast.error("Catatan wajib diisi");
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/review`, { status, note, expiry_date: expiry });
      toast.success("Review dokumen disimpan");
      onDone();
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const downloadDoc = async () => {
    try {
      const res = await api.get(`/documents/${doc.id}/signed-url`);
      window.open(window.location.origin + res.data.signed_url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="review-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="eyebrow">Review Dokumen</div>
            <h3 className="mt-1 font-display text-lg font-bold text-[#141414]">{doc.document_type_name}</h3>
            <p className="text-sm text-[#8A867C]">{doc.pilgrim_name} · {doc.product_name}</p>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-[#E5DEC9] p-3">
          <div className="flex-1 text-sm">
            <div className="font-semibold">{doc.file_name}</div>
            <div className="text-xs text-[#8A867C]">Versi {doc.version} · {Math.round((doc.size||0)/1024)} KB</div>
          </div>
          <button onClick={downloadDoc} className="rounded-lg border border-[#E5DEC9] p-2 text-[#4A4844] hover:bg-[#F4EFE6]" title="Lihat file">
            <Eye size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Status</label>
            <select className={inp} data-testid="review-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="approved">Disetujui</option>
              <option value="under_review">Sedang Direview</option>
              <option value="revision_required">Perlu Revisi</option>
              <option value="rejected">Ditolak</option>
            </select>
          </div>
          {status === "approved" && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#4A4844]">Tanggal Kedaluwarsa (opsional)</label>
              <input type="date" className={inp} value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="review-expiry" />
            </div>)}
          <div>
            <label className="mb-1 block text-xs font-semibold text-[#4A4844]">
              Catatan {needsNote && <span className="text-red-500">*</span>}
            </label>
            <textarea rows={3} className={inp} data-testid="review-note" placeholder={needsNote ? "Jelaskan alasan revisi/penolakan..." : "Catatan opsional..."}
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-[#E5DEC9] py-2 text-sm font-semibold">Batal</button>
            <button type="submit" disabled={busy} data-testid="review-submit"
              className={`flex-1 rounded-lg py-2 text-sm font-bold text-white ${status === "approved" ? "bg-green-600 hover:bg-green-700" : status === "rejected" ? "bg-red-600 hover:bg-red-700" : "bg-[#141414] hover:bg-[#333]"}`}>
              {busy ? "Menyimpan..." : "Simpan Review"}
            </button>
          </div>
        </form>
      </div>
    </div>);
}

export function DocumentsAdmin() {
  const { can } = useAuth();
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewDoc, setReviewDoc] = useState(null);

  const load = useCallback(() => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get("/admin/documents/queue", { params }).then((r) => setDocs(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/document-types").then((r) => setTypes(r.data)).catch(() => {});
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  // Ringkasan status
  const summary = useMemo(() => {
    const s = { total: docs.length, approved: 0, uploaded: 0, revision_required: 0, rejected: 0, not_uploaded: 0 };
    docs.forEach((d) => { if (s[d.status] !== undefined) s[d.status]++; });
    return s;
  }, [docs]);

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/signed-url`);
      window.open(window.location.origin + res.data.signed_url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Dokumen" breadcrumb={["Dokumen"]}>
      {/* Ringkasan */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Total", value: summary.total, color: "text-[#141414]" },
          { label: "Disetujui", value: summary.approved, color: "text-green-700" },
          { label: "Menunggu", value: summary.uploaded, color: "text-blue-700" },
          { label: "Perlu Revisi", value: summary.revision_required, color: "text-orange-600" },
        ].map((s) => (
          <Card key={s.label} className="text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="mt-0.5 text-xs text-[#8A867C]">{s.label}</div>
          </Card>))}
      </div>

      {/* Filter */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <select className={`${inp} max-w-xs`} data-testid="doc-status-filter" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Semua status</option>
            <option value="uploaded">Diunggah</option>
            <option value="under_review">Sedang Direview</option>
            <option value="revision_required">Perlu Revisi</option>
            <option value="approved">Disetujui</option>
            <option value="rejected">Ditolak</option>
            <option value="expired">Kedaluwarsa</option>
          </select>
          <span className="text-sm text-[#8A867C]">{docs.length} dokumen</span>
        </div>
      </Card>

      {/* Tabel dokumen */}
      <Table testid="document-table"
        columns={["Nama Jamaah", "Paket", "Jenis Dokumen", "Versi", "Status", "Reviewer", "Catatan", "Aksi"]}
        rows={docs.map((d) => (
          <tr key={d.id} className="border-t border-[#F4EFE6]" data-testid={`document-row-${d.id}`}>
            <td className="p-4 font-semibold text-[#141414]">{d.pilgrim_name}</td>
            <td className="p-4 text-sm text-[#4A4844]">{d.product_name}</td>
            <td className="p-4 text-sm">{d.document_type_name}</td>
            <td className="p-4 text-sm text-center">v{d.version || 1}</td>
            <td className="p-4"><DocStatusBadge status={d.status} /></td>
            <td className="p-4 text-xs text-[#8A867C]">{d.reviewer_email || "-"}</td>
            <td className="p-4 text-xs text-[#8A867C] max-w-[160px] truncate" title={d.review_note || ""}>{d.review_note || "-"}</td>
            <td className="p-4">
              <div className="flex gap-1">
                <button onClick={() => downloadDoc(d)} data-testid={`doc-view-${d.id}`}
                  className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]" title="Lihat file">
                  <Eye size={14} />
                </button>
                {can("document.review") && d.status !== "not_uploaded" && (
                  <button onClick={() => setReviewDoc(d)} data-testid={`doc-review-${d.id}`}
                    className="rounded-lg border border-[#E5DEC9] p-1.5 text-[#4A4844] hover:bg-[#F4EFE6]" title="Review">
                    <CheckCircle size={14} />
                  </button>)}
              </div>
            </td>
          </tr>))} />

      {/* Jenis Dokumen */}
      <Card className="mt-6">
        <div className="eyebrow">Jenis Dokumen Terdaftar</div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="document-types">
          {types.map((t) => <span key={t.id} className="rounded-full bg-[#F4EFE6] px-3 py-1 text-xs font-semibold text-[#141414]">{t.name}</span>)}
        </div>
      </Card>

      {reviewDoc && (
        <ReviewModal doc={reviewDoc} onClose={() => setReviewDoc(null)} onDone={() => { setReviewDoc(null); load(); }} />)}
    </ErpLayout>);
}
